import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  Eye,
  FileText,
  Lock,
  RefreshCcw,
  ShieldAlert,
  Unlock,
  Users,
} from 'lucide-react';
import {
  Card,
  CardTitle,
  EmptyState,
  MetricCard,
  PageHeader,
  PageSpinner,
  SkeletonKpiCard,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import type { ResponseEnvelope } from '@/types/api';
import { Endpoints } from '@/lib/api/endpoints';

interface StuckPlusOverview {
  totalSessions: number;
  lockedSessions: number;
  recoveredSessions: number;
  totalReveals: number;
  creditsConsumed: number;
  highRiskSessions: number;
}

interface StuckSession {
  id: string;
  sessionId: string;
  clientId: string;
  clientName?: string;
  attemptCount: number;
  riskScore?: number;
  status: string;
  revealCount: number;
  lastAttemptAt?: string;
  createdAt: string;
}

interface StuckSessionPage {
  content: StuckSession[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface RevealEvent {
  id: string;
  revealId: string;
  clientId: string;
  clientName?: string;
  sessionId: string;
  performedByDisplayName?: string;
  reason?: string;
  outcome: string;
  creditsDeducted: number;
  balanceBefore: number;
  balanceAfter: number;
  createdAt: string;
}

interface RevealPage {
  content: RevealEvent[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const OUTCOME_COLOR: Record<string, string> = {
  SUCCESS: 'bg-emerald-500/15 text-emerald-400',
  FAILED: 'bg-rose-500/15 text-rose-400',
  INSUFFICIENT_BALANCE: 'bg-amber-500/15 text-amber-400',
};

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'bg-amber-500/15 text-amber-400',
  LOCKED: 'bg-rose-500/15 text-rose-400',
  RESOLVED: 'bg-emerald-500/15 text-emerald-400',
  RECOVERED: 'bg-blue-500/15 text-blue-400',
};

type ActiveTab = 'sessions' | 'reveals';

export function AdminRecoveryIntelligencePage() {
  const [tab, setTab] = useState<ActiveTab>('sessions');
  const [sessionsPage, setSessionsPage] = useState(0);
  const [revealsPage, setRevealsPage] = useState(0);

  const overviewQuery = useQuery({
    queryKey: ['admin', 'stuck-plus', 'overview'],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<StuckPlusOverview>>(Endpoints.adminStuckPlus.overview())),
    staleTime: 60_000,
  });

  const sessionsQuery = useQuery({
    queryKey: ['admin', 'stuck-plus', 'sessions', sessionsPage],
    queryFn: async () =>
      unwrap(
        await api.get<ResponseEnvelope<StuckSessionPage>>(Endpoints.adminStuckPlus.sessions(), {
          params: { page: sessionsPage, size: 25 },
        }),
      ),
    staleTime: 30_000,
  });

  const revealsQuery = useQuery({
    queryKey: ['admin', 'stuck-plus', 'reveals', revealsPage],
    queryFn: async () =>
      unwrap(
        await api.get<ResponseEnvelope<RevealPage>>(Endpoints.adminStuckPlus.reveals(), {
          params: { page: revealsPage, size: 25 },
        }),
      ),
    enabled: tab === 'reveals',
    staleTime: 30_000,
  });

  const ov = overviewQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Recovery Intelligence"
        subtitle="Stuck+ session monitoring, secure reveals, and identity recovery across all clients"
        badge={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/15 px-3 py-1 text-xs font-semibold text-violet-400">
            <Brain className="h-3 w-3" />
            Stuck+
          </span>
        }
      />

      {/* KPI Strip */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {overviewQuery.isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonKpiCard key={i} />)
        ) : (
          <>
            <MetricCard label="Total Sessions" value={ov?.totalSessions ?? 0} icon={Users} />
            <MetricCard label="Locked" value={ov?.lockedSessions ?? 0} icon={Lock} tone="danger" />
            <MetricCard label="Recovered" value={ov?.recoveredSessions ?? 0} icon={Unlock} tone="success" />
            <MetricCard label="Reveals" value={ov?.totalReveals ?? 0} icon={Eye} tone="warning" />
            <MetricCard label="Credits Consumed" value={ov?.creditsConsumed ?? 0} icon={RefreshCcw} />
            <MetricCard label="High Risk" value={ov?.highRiskSessions ?? 0} icon={ShieldAlert} tone="danger" />
          </>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 rounded-xl border border-border bg-surface p-1" style={{ width: 'fit-content' }}>
        {(['sessions', 'reveals'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
              tab === t
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-fg-muted hover:text-fg'
            }`}
          >
            {t === 'sessions' ? 'Sessions' : 'Reveal Audit'}
          </button>
        ))}
      </div>

      {/* Sessions Table */}
      {tab === 'sessions' && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <CardTitle>
              <span className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-fg-muted" />
                Stuck Sessions
              </span>
            </CardTitle>
            {sessionsQuery.data && (
              <span className="text-xs text-fg-muted">
                {sessionsQuery.data.totalElements.toLocaleString()} total
              </span>
            )}
          </div>

          {sessionsQuery.isLoading ? (
            <PageSpinner />
          ) : !sessionsQuery.data?.content?.length ? (
            <EmptyState message="No stuck sessions recorded yet." icon={Lock} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>Client</TH>
                      <TH>Identity</TH>
                      <TH>Attempts</TH>
                      <TH>Risk</TH>
                      <TH>Status</TH>
                      <TH>Reveals</TH>
                      <TH>Last Activity</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {sessionsQuery.data.content.map((s) => (
                      <TR key={s.id}>
                        <TD className="font-medium text-sm">{s.clientName ?? s.clientId}</TD>
                        <TD>
                          <span className="font-mono text-xs tracking-widest text-fg-muted">
                            ************
                          </span>
                        </TD>
                        <TD className="text-center font-semibold">{s.attemptCount}</TD>
                        <TD>
                          {s.riskScore != null ? (
                            <span
                              className={`text-xs font-semibold ${
                                s.riskScore >= 70
                                  ? 'text-rose-400'
                                  : s.riskScore >= 40
                                  ? 'text-amber-400'
                                  : 'text-emerald-400'
                              }`}
                            >
                              {s.riskScore}
                            </span>
                          ) : (
                            <span className="text-fg-subtle text-xs">—</span>
                          )}
                        </TD>
                        <TD>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[s.status] ?? 'bg-surface-muted text-fg-muted'}`}
                          >
                            {s.status}
                          </span>
                        </TD>
                        <TD className="text-center">{s.revealCount}</TD>
                        <TD className="text-xs text-fg-muted">
                          {s.lastAttemptAt
                            ? new Date(s.lastAttemptAt).toLocaleString()
                            : new Date(s.createdAt).toLocaleString()}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
              <Pagination
                page={sessionsPage}
                totalPages={sessionsQuery.data.totalPages}
                onChange={setSessionsPage}
              />
            </>
          )}
        </Card>
      )}

      {/* Reveal Audit Table */}
      {tab === 'reveals' && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <CardTitle>
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-fg-muted" />
                Reveal Audit Log
              </span>
            </CardTitle>
            {revealsQuery.data && (
              <span className="text-xs text-fg-muted">
                {revealsQuery.data.totalElements.toLocaleString()} total
              </span>
            )}
          </div>

          {revealsQuery.isLoading ? (
            <PageSpinner />
          ) : !revealsQuery.data?.content?.length ? (
            <EmptyState message="No reveal events recorded yet." icon={FileText} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>Reveal ID</TH>
                      <TH>Client</TH>
                      <TH>Performed By</TH>
                      <TH>Outcome</TH>
                      <TH>Credits</TH>
                      <TH>Balance After</TH>
                      <TH>Reason</TH>
                      <TH>Date</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {revealsQuery.data.content.map((r) => (
                      <TR key={r.id}>
                        <TD className="font-mono text-xs">{r.revealId}</TD>
                        <TD className="text-sm">{r.clientName ?? r.clientId}</TD>
                        <TD className="text-sm">{r.performedByDisplayName ?? '—'}</TD>
                        <TD>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${OUTCOME_COLOR[r.outcome] ?? 'bg-surface-muted text-fg-muted'}`}
                          >
                            {r.outcome}
                          </span>
                        </TD>
                        <TD className="text-center font-semibold text-sm">{r.creditsDeducted}</TD>
                        <TD className="text-center text-sm">{r.balanceAfter.toLocaleString()}</TD>
                        <TD className="max-w-[180px] truncate text-xs text-fg-muted">
                          {r.reason ?? '—'}
                        </TD>
                        <TD className="text-xs text-fg-muted">
                          {new Date(r.createdAt).toLocaleString()}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
              <Pagination
                page={revealsPage}
                totalPages={revealsQuery.data.totalPages}
                onChange={setRevealsPage}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-fg-muted">
      <button
        className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-muted disabled:opacity-40"
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >
        Previous
      </button>
      <span>
        Page {page + 1} of {totalPages}
      </span>
      <button
        className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-muted disabled:opacity-40"
        disabled={page >= totalPages - 1}
        onClick={() => onChange(page + 1)}
      >
        Next
      </button>
    </div>
  );
}
